import classNames from 'classnames'
import * as React from 'react'

import Photo, { PhotoSrcProps } from 'components/Photo/Photo'
import Video from 'components/Video'

import './Section.scss'

export enum SectionColors {
  almond = 'almond',
  blue = 'blue',
  rose = 'rose',
  lightTomato = 'light-tomato',
  coral = 'coral',
  glory = 'morning-glory',
  cinderella = 'cinderella',
}

export enum Size {
  xs = 'xs',
  s = 's',
  m = 'm',
  l = 'l',
  xl = 'xl',
  initial = 'initial',
  initialExpand = 'initial-expand',
  fullWidth = 'full-width',
}

export enum SectionType {
  solid = 'solid',
  solidPink = 'solid-pink',
  solidTurquoise = 'solid-turquoise',
  solidLightPink = 'solid-light-pink',
  pattern = 'pattern',
  white = 'white',
  transparentColor = 'transparent-color',
  transparentWhite = 'transparent-white',
  grey = 'grey',
  photo = 'photo',
  video = 'video',
  background = 'background',
  message = 'message',
  whiteMessage = 'white-message',
}

interface DecorationImage extends PhotoSrcProps {
  width: number
  top?: number
  bottom?: number
  left?: number
  right?: number
  desktopOnly?: boolean
  isCovered?: boolean
}

interface SectionBackgroundImage {
  url: string
  position: string
  blurMobile?: boolean
}

interface SectionProps {
  type?: SectionType
  size?: Size
  style?: React.CSSProperties
  patternImage?: string
  leftPatternImage?: string
  rightPatternImage?: string
  bottomPatternImage?: string
  decorationImages?: DecorationImage[]
  src?: PhotoSrcProps
  noPadding?: boolean
  noBottomPadding?: boolean
  noOverflow?: boolean
  borderBottom?: boolean
  homeStatsGap?: boolean
  backgroundImage?: SectionBackgroundImage
  sectionRef?: (instance: HTMLDivElement) => void
  color?: SectionColors
  className?: string
  bottomImageClassName?: string
  children?: React.ReactNode | undefined
}

const Section: React.FC<SectionProps> = props => {
  const patternImage = `url(${props.patternImage})`
  const leftPatternImage = `url(${props.leftPatternImage})`
  const rightPatternImage = `url(${props.rightPatternImage})`
  const bottomPatternImage = `url(${props.bottomPatternImage})`

  const className = classNames(
    'srprs-section',
    props?.className ? `${props.className}` : '',
    props.type ? `type-${props.type}` : '',
    props.size ? `size-${props.size}` : '',
    props.noPadding ? 'no-padding' : '',
    props.noBottomPadding ? 'no-bottom-padding' : '',
    props.noOverflow ? 'no-overflow' : '',
    props.borderBottom ? 'has-border-bottom' : '',
    props.homeStatsGap ? 'home-stats-gap' : '',
    props.color ? `section-color-${props.color}` : '',
  )

  const createBackgroundImageBlock = (img: SectionBackgroundImage) => {
    const divClassName = classNames(
      `background-image`,
      img.blurMobile ? `blur-mobile` : ``,
    )
    return (
      <div
        className={divClassName}
        style={{
          backgroundImage: `url(${img.url})`,
          backgroundPosition: img.position,
        }}
      />
    )
  }

  return (
    <section
      className={className}
      ref={props.sectionRef}
      style={
        props.patternImage
          ? { backgroundImage: patternImage, ...props.style }
          : props.style
      }
    >
      {props.decorationImages
        ? props.decorationImages.map(img => {
            const extraStyle = {
              maxWidth: img.width,
              left: img.left !== undefined ? `${img.left}px` : `auto`,
              right: img.right !== undefined ? `${img.right}px` : `auto`,
              top: img.top !== undefined ? `${img.top}px` : `auto`,
              bottom: img.bottom !== undefined ? `${img.bottom}px` : `auto`,
            }
            const imageClasses = classNames(
              `decoration-image${img.desktopOnly ? `-desktop` : ''}`,
              img.isCovered ? `is-covered` : '',
            )
            return (
              <div className={imageClasses} style={extraStyle}>
                <figure>
                  <picture>
                    {img.src1920 && (
                      <source
                        media="(min-width: 1920px)"
                        srcSet={img.src1920}
                      />
                    )}
                    {img.src1600 && (
                      <source
                        media="(min-width: 1600px)"
                        srcSet={img.src1600}
                      />
                    )}
                    {img.src1280 && (
                      <source
                        media="(min-width: 1280px)"
                        srcSet={img.src1280}
                      />
                    )}
                    {img.src1024 && (
                      <source
                        media="(min-width: 1024px)"
                        srcSet={img.src1024}
                      />
                    )}
                    {img.src768 && (
                      <source media="(min-width: 768px)" srcSet={img.src768} />
                    )}
                    <img src={img.src} alt={img.alt} />
                  </picture>
                </figure>
              </div>
            )
          })
        : null}
      {props.leftPatternImage ? (
        <div
          className="left-pattern"
          style={{ backgroundImage: leftPatternImage }}
        />
      ) : null}
      {props.src && props.type === SectionType.video ? (
        <Video alt="" {...props.src} />
      ) : null}
      {props.src && props.type === SectionType.photo ? (
        <Photo alt="" {...props.src} />
      ) : null}
      {props.backgroundImage
        ? createBackgroundImageBlock(props.backgroundImage)
        : null}
      <div className="wrapper-inner">{props.children}</div>
      {props.rightPatternImage ? (
        <div
          className="right-pattern"
          style={{ backgroundImage: rightPatternImage }}
        />
      ) : null}
      {props.bottomPatternImage ? (
        <div
          className={`bottom-pattern ${
            props.bottomImageClassName ? props?.bottomImageClassName : ''
          }`}
          style={{ backgroundImage: bottomPatternImage }}
        />
      ) : null}
    </section>
  )
}

Section.defaultProps = {
  type: SectionType.white,
}

export default Section
